import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  CheckBoxOutlineBlankOutlined,
  CheckBoxOutlined,
  FileDownloadOutlined,
} from "@mui/icons-material";
import {
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
  Box,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  TextField,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";

import AppConfirmDialog from "components/Shared/AppConfirmDialog";
import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useLazyListGetDocumentsExpiryQuery } from "store/services/reportApi";
import { useLazyGetDdlVendorProfileQuery } from "store/services/vendorProfileApi";
import { exportExcelFile } from "utils/exportExcelFile";
import { useAuth } from "hooks";
import moment from "moment";

const EXPORT_DATA_INITIAL_STATE = {
  dateRange: [],
  vendor: null,
  expiryFilter: 'week'
};

const ExportVendorDocumentExpiryReport = ({ isBusy = false }) => {
  const [showDialog, setShowDialog] = React.useState(false);
  const [showVendorForm, setShowVendorForm] = React.useState(false);
  const { viewEntity } = useAuth();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    defaultValues: EXPORT_DATA_INITIAL_STATE,
  });

  const selectedFilter = watch('expiryFilter');

  // RTK Query implementation
  const [fetchDocuments, { isLoading: isFetching }] = useLazyListGetDocumentsExpiryQuery();
  const [fetchVendors, { data: vendors, isLoading: loadingVendors }] =
    useLazyGetDdlVendorProfileQuery();

  useEffect(() => {
    if (!showDialog) return;
    if (viewEntity?.entity?._id && viewEntity?.entity?.account?._id) {
      fetchVendors({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      });
    }
  }, [fetchVendors, viewEntity?.entity?._id, viewEntity?.entity?.account?._id, showDialog]);

  const handleDownload = async (data) => {
    try {
      let params = {
        ...(showVendorForm && data.vendor && { vendorId: data.vendor._id })
      };

      let fileNameParts = ["Vendor_Document_Expiry_Report"];

      // Add vendor name to filename if filtered by vendor
      if (showVendorForm && data.vendor) {
        const vendorName = data.vendor.shopName || data.vendor.ownerName || 'Vendor';
        fileNameParts.push(vendorName.replace(/\s+/g, ''));
      }

      if (data.expiryFilter === 'custom' && data.dateRange?.length === 2) {
        params = {
          ...params,
          startDate: moment(data.dateRange[0]).format('YYYY-MM-DD'),
          endDate: moment(data.dateRange[1]).format('YYYY-MM-DD')
        };
        fileNameParts.push(
          `${moment(data.dateRange[0]).format('MMM_DD')}_to_${moment(data.dateRange[1]).format('MMM_DD')}`
        );
      } else {
        const days = data.expiryFilter === 'week' ? 7 : 30;
        params = { ...params, daysThreshold: days };
        fileNameParts.push(data.expiryFilter === 'week' ? '1_Week' : '1_Month');
      }

      const responseData = await fetchDocuments(params).unwrap();

      if (!responseData?.data || responseData.data.length === 0) {
        toast.info("No documents found for the selected criteria.");
        return;
      }

      const formattedData = responseData.data.map((row, index) => ({
        SN: index + 1,
        "Document Title": row.title || "N/A",
        "Owner Name": row.sourceRef?.ownerName || "N/A",
        "Shop Name": row.sourceRef?.shopName || "N/A",
        "Document Type": row.vendorDocumentType?.name || "N/A",
        "Expiry Date": row.expiryDate 
          ? moment(row.expiryDate).format("YYYY-MM-DD") 
          : "N/A",
        "Days Left": row.daysUntilExpiry || "N/A",
      }));

      // Clean filename parts and join with underscores
      const cleanFileNameParts = fileNameParts.map(part => 
        part.replace(/[^a-zA-Z0-9_]/g, '').trim()
      ).filter(part => part.length > 0);

      const baseFileName = cleanFileNameParts.join('_');
      let fileName = baseFileName;

      exportExcelFile({
        sheetData: formattedData,
        fileName: fileName,
      });

      toast.success("Report downloaded successfully");
      handleReset();
    } catch (error) {
      toast.error("Error downloading report: " + (error.message || "Unknown error"));
    }
  };

  const handleReset = () => {
    setShowDialog(false);
    setShowVendorForm(false);
    reset(EXPORT_DATA_INITIAL_STATE);
  };

  return (
    <>
      <Tooltip title="Export Excel" arrow>
        <IconButton 
          color="success" 
          onClick={() => setShowDialog(true)}
          disabled={isBusy}
        >
          <FileDownloadOutlined />
        </IconButton>
      </Tooltip>

      <AppConfirmDialog
        open={showDialog}
        onClose={handleReset}
        onConfirm={handleSubmit(handleDownload)}
        title="Download Document Expiry Report"
        dialogIcon={() => <FileDownloadOutlined color="success" />}
        confirmText="Download"
        confirmDisabled={isFetching || isBusy}
      >
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showVendorForm}
                onChange={(e) => {
                  setShowVendorForm(e.target.checked);
                  if (!e.target.checked) {
                    reset({ vendor: null });
                  }
                }}
                icon={<CheckBoxOutlineBlankOutlined />}
                checkedIcon={<CheckBoxOutlined />}
              />
            }
            label="Filter by Vendor"
          />
        </Box>

        {showVendorForm && (
          <AppForm loading={isBusy}>
            <AppForm.Content>
              <Box sx={{ mb: 2 }}>
                <Controller
                  name="vendor"
                  control={control}
                  rules={{
                    required: showVendorForm ? "Please select Vendor." : false,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <BaseAutocomplete
                      fullWidth
                      label="Vendor"
                      required={showVendorForm}
                      value={value || null}
                      onChange={(_, data) => onChange(data)}
                      getOptionLabel={(option) =>
                        option ? `${option?.ownerName} - ${option?.shopName}` : ""
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      options={vendors || []}
                      loading={loadingVendors}
                      loadingText="Loading Vendors..."
                      helperText={errors?.vendor?.message}
                      error={!!errors?.vendor}
                    />
                  )}
                />
              </Box>
            </AppForm.Content>
          </AppForm>
        )}

        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <FormLabel component="legend">Expiry Range</FormLabel>
          <Controller
            name="expiryFilter"
            control={control}
            render={({ field }) => (
              <RadioGroup {...field} row>
                <FormControlLabel 
                  value="week" 
                  control={<Radio />} 
                  label="1 Week" 
                />
                <FormControlLabel 
                  value="month" 
                  control={<Radio />} 
                  label="1 Month" 
                />
                <FormControlLabel 
                  value="custom" 
                  control={<Radio />} 
                  label="Custom Date Range" 
                />
              </RadioGroup>
            )}
          />
        </FormControl>

        {selectedFilter === 'custom' && (
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Controller
                name="dateRange[0]"
                control={control}
                rules={{
                  validate: (value) => {
                    if (selectedFilter === 'custom' && !value) {
                      return "Please select a start date";
                    }
                    return true;
                  }
                }}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <DesktopDatePicker
                    label="Start Date"
                    inputFormat="MM/DD/YYYY"
                    value={value || null}
                    onChange={(date) => onChange(date)}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        size="small" 
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
              <Controller
                name="dateRange[1]"
                control={control}
                rules={{
                  validate: (value) => {
                    if (selectedFilter === 'custom' && !value) {
                      return "Please select an end date";
                    }
                    const startDate = watch('dateRange[0]');
                    if (selectedFilter === 'custom' && startDate && value && moment(value).isBefore(moment(startDate))) {
                      return "End date must be after start date";
                    }
                    return true;
                  }
                }}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <DesktopDatePicker
                    label="End Date"
                    inputFormat="MM/DD/YYYY"
                    value={value || null}
                    onChange={(date) => onChange(date)}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        size="small" 
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                    shouldDisableDate={(date) => watch('dateRange[0]') && date < moment(watch('dateRange[0]'))}
                    disabled={!watch('dateRange[0]')}
                  />
                )}
              />
            </Box>
          </LocalizationProvider>
        )}
      </AppConfirmDialog>
    </>
  );
};

export default ExportVendorDocumentExpiryReport;